import { useEffect, useMemo, useState } from "react";
import Table from "../../Common/Table";
import Pagination from "../../Common/Pagination";
import apiService, { handleError } from "../../Common/ApiCommon/ApiService";
import toast from "react-hot-toast";

const LeaveStatus = ({ staffId, statusFilter }) => {
  const [leaveData, setLeavesData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [loading, setLoading] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchLeaves = async () => {
    try {
      setLoading(true);
      const response = await apiService.get(`/leaves?staff_user_id=${staffId}`);
      setLeavesData(response.data);
    } catch (errors) {
      handleError(errors);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeaves();
  }, []);

  const handleLeaveAction = async (id, actionType) => {
    try {
      setIsSubmitting(true);
      const response = await apiService.post(`leaves/${id}/leave_approval`, {
        status: actionType === "approve" ? "accepted" : "rejected",
      });
      toast.success(response.message || `${actionType}ed successfully`);
      setModalData(null);
      fetchLeaves();
    } catch (errors) {
      handleError(errors);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Define table columns
  const columns = useMemo(() => {
    const baseColumns = [
      {
        Header: "id",
        accessor: "ids",
      },
      {
        Header: "Leave Type",
        accessor: "leave_type",
      },
      {
        Header: "Duration",
        accessor: "duration",
        Cell: ({ row }) => {
          const {
            from_date,
            to_date,
            from_date_session,
            to_date_session,
            leave_days,
          } = row.original;
          const formatDate = (dateStr) => {
            const options = { day: "2-digit", month: "short", year: "numeric" };
            return new Date(dateStr).toLocaleDateString("en-GB", options);
          };

          return (
            <span>
              {formatDate(from_date)} ( {from_date_session}) to{" "}
              {formatDate(to_date)} ( {to_date_session})
            </span>
          );
        },
      },
      {
        Header: "No. of Days",
        accessor: "leave_days",
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: ({ value }) => {
          return (
            <div className="flex justify-start md:justify-center">
              <p
                className={`text-xs sm:text-sm font-medium flex justify-center items-center rounded-3xl w-[100px] px-4 py-[6px]  ${
                  value === "Pending"
                    ? "text-yellow bg-yellow bg-opacity-[10%]"
                    : value === "Approved"
                    ? "text-green bg-green bg-opacity-[10%]"
                    : "text-red bg-red bg-opacity-[10%]"
                }`}
              >
                {value}
              </p>
            </div>
          );
        },
      },
    ];

    // display only if statusFilter is "pending"
    if (statusFilter === "pending") {
      baseColumns.push({
        Header: "Action",
        accessor: "action",
        disableSortBy: true,
        Cell: ({ row }) => {
          return (
            <div className="flex justify-center gap-3">
              <button
                onClick={() =>
                  setModalData({ id: row.original.id, type: "approve" })
                }
                className="px-2 py-[6px] text-sm rounded-md bg-green text-white"
              >
                Approve
              </button>
              <button
                onClick={() =>
                  setModalData({ id: row.original.id, type: "reject" })
                }
                className="px-2 py-[6px] text-sm rounded-md bg-red text-white"
              >
                Reject
              </button>

              {modalData && (
                <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-10">
                  <div className="bg-white rounded-lg p-6 w-[310px] sm:w-[350px] shadow-lg relative">
                    <p className="text-lg font-semibold text-center mb-8">
                      Are you sure you want to {modalData.type} this leave?
                    </p>
                    <div className="flex justify-center gap-4 mt-4">
                      <button
                        className="w-[75px] sm:w-[93px] py-2 text-sm bg-white text-green border border-green rounded-lg"
                        onClick={() => setModalData(null)}
                      >
                        Close
                      </button>
                      <button
                        onClick={() =>
                          handleLeaveAction(modalData.id, modalData.type)
                        }
                        type="button"
                        className={`w-[75px] sm:w-[93px] text-sm py-2 rounded-lg ${
                          isSubmitting
                            ? "bg-green50 cursor-not-allowed"
                            : "bg-green text-white"
                        }`}
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? "Confirming..." : "Confirm"}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        },
      });
    }

    return baseColumns;
  }, [modalData, statusFilter, isSubmitting]);

  const data = useMemo(() => {
    if (!Array.isArray(leaveData)) return [];

    return leaveData
      .filter((item) => {
        if (statusFilter === "pending") return item.status === "pending";
        if (statusFilter === "history")
          return item.status === "accepted" || item.status === "rejected";
        return true;
      })
      .map((data, index) => ({
        ids: index + 1,
        id: data.id,
        leave_type: data.leave_type,
        leave_days: data.leave_days,
        from_date_session: data.from_date_session,
        to_date_session: data.to_date_session,
        from_date: data.from_date,
        to_date: data.to_date,
        status:
          data.status === "pending"
            ? "Pending"
            : data.status === "accepted"
            ? "Approved"
            : data.status === "rejected"
            ? "Rejected"
            : "",
      }));
  }, [leaveData, statusFilter]);

  // Paginate the data
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = data.slice(indexOfFirstRow, indexOfLastRow);

  return (
    <div>
      <div className="flex flex-col justify-start bg-white rounded-2xl px-0 sm:p-4 ">
        <h1 className="main_heading text-2xl mb-5 pl-4 mt-3 sm:pl-0">
          <p>{statusFilter === "pending" ? "Leave Status" : "Leave History"}</p>
        </h1>

        {loading ? (
          <div className="flex justify-center items-center my-4">
            <p className="text-green700 font-medium">Loading data...</p>
          </div>
        ) : (
          <div className="mb-6 overflow-auto horizontalscroll bg-G20">
            <Table columns={columns} data={currentRows} />
          </div>
        )}

        {/* Pagination */}
        <Pagination
          currentPage={currentPage}
          rowsPerPage={rowsPerPage}
          totalRows={data.length}
          onPageChange={setCurrentPage}
          onRowsPerPageChange={(newRows) => {
            setRowsPerPage(newRows);
            setCurrentPage(1);
          }}
        />
      </div>
    </div>
  );
};

export default LeaveStatus;
